import { useState, useEffect } from "react"
import { Input, Button, message } from "antd"
import { multiFactor, TotpMultiFactorGenerator, signOut } from "firebase/auth"
import { auth } from "../../../helpers/auth"
import { generateTotp } from "../../../helpers/auth/login"
import QRCode from "qrcode";
import { useRef } from "react"
import nookies from "nookies"; 

export async function GenerateTotpQr() {
  try {
    const user = auth.currentUser;

    if (!user) {throw new Error("Usuario no autenticado")}

    const session = await multiFactor(user).getSession();

    const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);

    const qrCodeUrl = totpSecret.generateQrCodeUrl(
      user.email,
      "Ficosec"
    )

    return {
      totpSecret,
      qrCodeUrl
    };
  } catch (error) {
    console.error("Error generando TOTP:", error);
    throw error;
  }
}


async function forceLogoutAndRedirect(reason) {
  try {
    await signOut(auth);

    const cookies = nookies.get(null);
    Object.keys(cookies).forEach((cookieName) => {
      nookies.destroy(null, cookieName, { path: "/" });
    })
    localStorage.clear();
  } catch (logoutError) {
    console.error("Error cerrando sesión:", logoutError);
  } finally {
    const loggingOut = message.loading("Cerrando sesión")
    window.location.href = "/"
    loggingOut()
  }
}

export default function EnableTotp({ onComplete }) {
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) {
      return;
    }

    initialized.current = true;

    const init = async () => {
      try {
        setLoading(true);

        const { totpSecret, qrCodeUrl } = await GenerateTotpQr()
        const qrImageDataUrl = await QRCode.toDataURL(qrCodeUrl)

        setSecret(totpSecret)
        setQr(qrImageDataUrl)
      } catch (error) {
        console.error("Error generando QR:", error)
        setQr(null)
      } finally {
        setLoading(false)
      }
    };

    init()
  }, [])

  const handleVerify = async () => {
    if (code.length !== 6 || !/^\d+$/.test(code)) {
      message.warning("Ingresa un código válido de 6 dígitos");
      return;
    }

    try {
      setVerifying(true);

      const user = auth.currentUser

      if (!user) {
        message.error("No hay usuario autenticado");
        return;
      }

      if (!secret) {
        message.error("Error: No hay secreto TOTP disponible");
        return;
      }

      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secret,
        code
      )

      await multiFactor(user).enroll(assertion, "Authenticator App")


      setCode("");
      message.success("Autenticador de dos factores activado correctamente ✅")

      if (onComplete) {
        onComplete()
      }
    } catch (error) {

      if (error?.code === "auth/invalid-verification-code") {
        setCode("");
        message.error("Código incorrecto. Verifica el código que se muestra en tu aplicación de autenticación e inténtalo de nuevo.")
        return;
      }

      if (error?.code === "auth/too-many-enrollment-attempts") {
        message.error(
          "Se alcanzó el límite de intentos de autenticación. " +
          "Por seguridad, debes iniciar sesión nuevamente para continuar.",
          4)

        setCode("");

        setTimeout(() => {
          forceLogoutAndRedirect("totp-too-many-enrollment-attempts");
        }, 1500)

        return
      }

      if (error?.code === "auth/multi-factor-info-not-found") {
        message.error("Error en la configuración MFA")
        return
      }

      message.error("Error al verificar el código: " + error.message)
    } finally {
      setVerifying(false)
    }
  }


  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <p>Generando código QR seguro...</p>
      </div>
    )
  }


  if (!qr) {
    return (
      <div style={{ textAlign: "center", color: "red" }}>
        <p>Error al generar el QR</p>
        <Button onClick={() => window.location.reload()}>Reintentar</Button>
      </div>
    );
  }

  return (
    <div
      style={{
        textAlign: "center",
        maxWidth: "400px",
        margin: "0 auto",
        padding: "20px"
      }}
    >
      <h3>Configurar Autenticación de Dos Factores</h3>

      <p style={{ marginBottom: "20px" }}>
        1. Escanea el código QR con tu aplicación de autenticación
        (Google Authenticator, Microsoft Authenticator, etc.)
      </p>

      <div
        style={{
          background: "#f5f5f5",
          padding: "20px",
          borderRadius: "8px",
          marginBottom: "20px"
        }}
      >
        <img
          src={qr}
          width={200}
          height={200}
          alt="Código QR para TOTP"
          style={{ border: "2px solid #fff", borderRadius: "8px" }}
        />
      </div>

      <p style={{ marginBottom: "10px" }}>
        2. Ingresa el código de 6 dígitos de tu aplicación
      </p>

      <Input
        placeholder="Ej: 123456"
        value={code}
        onChange={(e) =>
          setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
        }
        maxLength={6}
        style={{
          width: "150px",
          margin: "0 auto 10px",
          textAlign: "center",
          fontSize: "20px"
        }}
        disabled={verifying}
      />

      <Button
        type="primary"
        onClick={handleVerify}
        loading={verifying}
        disabled={code.length !== 6 || verifying}
        style={{ marginTop: 10 }}
        block
      >
        {verifying ? "Verificando..." : "Verificar y Activar"}
      </Button>

      <p style={{ marginTop: "20px", fontSize: "12px", color: "#999" }}>
        Una vez activado, necesitarás tu código de autenticación cada vez
        que inicies sesión.
      </p>
    </div>
  );
}
